// src/pages/PublicApplicationForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../lib/firebase"; // ✅ shared us-central1 Functions instance
import "./PublicApplicationForm.css";

const normalizeEmail = (e) => (e || "").trim().toLowerCase();

// Trinity-ish colors
const COLORS = {
  purple: "#5B2D90",
  green: "#2FA15C",
  ink: "#0F172A",
  slate: "#334155",
  border: "#E5E7EB",
  bg: "#FFFFFF",
  soft: "#F8FAFC",
  danger: "#B91C1C",
};

export default function PublicApplicationForm({ initialEmail = "", onDone }) {
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");

  const [emailChecked, setEmailChecked] = useState(false);
  const [showEditPrompt, setShowEditPrompt] = useState(false);
  const [pinPrompt, setPinPrompt] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [existingDocId, setExistingDocId] = useState(null);

  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const [step, setStep] = useState(0);

  const [confirm, setConfirm] = useState({ show: false, title: "", message: "" });

  // ✅ if launched from Home modal, skip internal email gate UI
  const [autoStarted, setAutoStarted] = useState(false);

  // Store existing doc for reliable PIN usage
  const existingDocRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    isEmployee: "", // Yes/No
    employeeNumber: "", // required if isEmployee === "Yes"
    ssn: "", // PIN (last4)
    position: "",
    phone: "",
    email: "",
    employer: "",
    sponsorHospital: "",
    dea: "",
    npi: "",
    businessAddress: "",
    businessPhone: "",
    assignment: [],
    availability: [],
    shirtSize: "",
    workedBefore: "",
    pastWorkYear: "",
    comments: "",
  });

  // -------------------- Cloud Functions callables --------------------
// ✅ PUBLIC flow uses V2 where available (submit is still V1)
const checkApplicationExists = useMemo(
  () => httpsCallable(functions, "checkApplicationExistsV2"),
  []
);

const getApplicationForEdit = useMemo(
  () => httpsCallable(functions, "getApplicationForEditV2"),
  []
);

// ✅ submit is still Gen1/V1 in your project right now
const submitApplicationPublic = useMemo(
  () => httpsCallable(functions, "submitApplicationPublic"),
  []
);

const updateApplicationPublic = useMemo(
  () => httpsCallable(functions, "updateApplicationPublicV2"),
  []
);

  const positions = useMemo(
  () => [
    "Tech",
    "EMR",
    "EMT",
    "AEMT",
    "Paramedic",
    "RN",
    "PA",
    "APRN",
    "Resident",
    "Physician",
    "Dispatcher",
    "Student",
    "Volunteer",
  ],
  []
);

  const assignmentOptions = useMemo(
    () => ["Medical Headquarters", "On Course Aid Station", "Cart Team", "Bike Team", "No Preference"],
    []
  );

  const availabilityOptions = useMemo(
    () => [
      "Monday June 22, 2026",
      "Tuesday June 23, 2026",
      "Wednesday June 24, 2026",
      "Thursday June 25, 2026",
      "Friday June 26, 2026",
      "Saturday June 27, 2026",
      "Sunday June 28, 2026",
    ],
    []
  );

  const shirtSizes = useMemo(() => ["XS", "S", "M", "L", "XL", "2XL", "3XL"], []);

  // ---------- Scroll-to-top (modal-friendly) ----------
  const scrollModalToTop = () => {
    const modalBody = document.getElementById("apply-modal-body");
    if (modalBody) {
      modalBody.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    scrollModalToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ✅ Also scroll to top when these screens appear
  useEffect(() => {
    if (showEditPrompt || pinPrompt) scrollModalToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditPrompt, pinPrompt]);

  // ---------- Helpers ----------
  const resetAll = () => {
    setFormData({
      name: "",
      address: "",
      isEmployee: "",
      employeeNumber: "",
      ssn: "",
      position: "",
      phone: "",
      email: "",
      employer: "",
      sponsorHospital: "",
      dea: "",
      npi: "",
      businessAddress: "",
      businessPhone: "",
      assignment: [],
      availability: [],
      shirtSize: "",
      workedBefore: "",
      pastWorkYear: "",
      comments: "",
    });
    setEmail("");
    setPin("");
    setEmailChecked(false);
    setPinPrompt(false);
    setShowEditPrompt(false);
    setEditMode(false);
    setExistingDocId(null);
    existingDocRef.current = null;
    setStatus("");
    setBusy(false);
    setStep(0);
    setConfirm({ show: false, title: "", message: "" });
    setAutoStarted(false);
  };

  const isFieldVisible = (field) => {
    const pos = formData.position;
    if (field === "sponsorHospital") return pos === "AEMT" || pos === "Paramedic";
    if (["dea", "npi", "businessAddress", "businessPhone"].includes(field)) {
      return pos === "Physician" || pos === "APRN" || pos === "PA";
    }
    return false;
  };

  // ✅ Assignment prefs only for EMT/AEMT/Paramedic
  const isAssignmentEligible = () => ["EMT", "AEMT", "Paramedic"].includes(formData.position);

  // ✅ Force all other positions to Medical HQ only
  useEffect(() => {
    if (!formData.position) return;

    if (!isAssignmentEligible()) {
      setFormData((p) => ({
        ...p,
        assignment: ["Medical Headquarters"],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.position]);

  const showConfirmation = (title, message) => {
    setConfirm({ show: true, title, message });
    setStatus("");
    scrollModalToTop();
  };

  const closeAfterSuccess = () => {
    if (onDone) onDone();
    resetAll();
  };

  // ---------- Toggle buttons ----------
  const toggleInArray = (field, value) => {
    setFormData((prev) => {
      const arr = Array.isArray(prev[field]) ? prev[field] : [];
      const exists = arr.includes(value);
      const next = exists ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, [field]: next };
    });
  };

  const toggleAvailabilitySelectAll = () => {
    setFormData((p) => {
      const allSelected = Array.isArray(p.availability) && p.availability.length === availabilityOptions.length;
      return { ...p, availability: allSelected ? [] : [...availabilityOptions] };
    });
  };

  // ---------- Email gate (via callable) ----------
  const handleEmailCheck = async (emailOverride) => {
    const emailKey = normalizeEmail(emailOverride ?? email);
    if (!emailKey) return;

    setBusy(true);
    setStatus("");

    try {
      const res = await checkApplicationExists({ email: emailKey });
      const exists = !!res?.data?.exists;

      if (exists) {
        setExistingDocId(emailKey);
        setShowEditPrompt(true);
        setPinPrompt(false);
        setEditMode(false);

        // Don't preload any application data here (keeps PII private until PIN)
        existingDocRef.current = null;
      } else {
        setFormData((prev) => ({ ...prev, email: emailKey }));
        setEmailChecked(true);
        setStep(0);
      }
    } catch (err) {
      console.error(err);
      const code = err?.code ? ` (${err.code})` : "";
      setStatus(`❌ Unable to check application. Please try again.${code}`);
      setAutoStarted(false);
    } finally {
      setBusy(false);
    }
  };

  // Auto-prefill + auto-check from modal
  const autoCheckedRef = useRef(false);
  useEffect(() => {
    const incoming = normalizeEmail(initialEmail);
    if (!incoming) return;

    setEmail(incoming);
    setAutoStarted(true); // ✅ hides internal email gate

    if (!autoCheckedRef.current) {
      autoCheckedRef.current = true;
      handleEmailCheck(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmail]);

  // ---------- Edit / PIN ----------
  const handleEditChoice = (choice) => {
    if (choice === "yes") {
      setPinPrompt(true);
      setShowEditPrompt(false);
      setStatus("");
      scrollModalToTop();
    } else {
      resetAll();
      if (onDone) onDone();
    }
  };

  const handlePINVerify = async () => {
    const emailKey = normalizeEmail(existingDocId || formData.email || email);
    const entered = (pin || "").trim();

    if (!emailKey) return setStatus("❌ Missing email.");
    if (entered.length !== 4) return setStatus("❌ Enter your 4-digit PIN.");

    setBusy(true);
    setStatus("");

    try {
      const res = await getApplicationForEdit({ email: emailKey, pin: entered });
      const app = res?.data?.application;

      if (!app) {
        setStatus("❌ Application not found.");
        return;
      }

      existingDocRef.current = app;

      // Mirror PIN into ssn for edit flows + update rules
      const pinFromDoc = String(app.pinLast4 || app.ssn || "").trim();

      setFormData({
        ...app,
        ssn: pinFromDoc,
        isEmployee: app.isEmployee || "",
        employeeNumber: app.employeeNumber || "",
        assignment: Array.isArray(app.assignment) ? app.assignment : [],
        availability: Array.isArray(app.availability) ? app.availability : [],
      });

      setEditMode(true);
      setEmailChecked(true);
      setPinPrompt(false);
      setStatus("");
      setStep(0);
      scrollModalToTop();
    } catch (err) {
      console.error(err);
      setStatus("❌ Incorrect PIN");
    } finally {
      setBusy(false);
    }
  };

  // ---------- Inputs ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  // ---------- Step validation ----------
  const step0Valid = () => {
    if (!formData.name || !formData.address || !formData.phone || !formData.ssn || !formData.position) return false;
    if (String(formData.ssn).length !== 4) return false;

    // ✅ Trinity employee question + conditional employee #
    if (!formData.isEmployee) return false;
    if (formData.isEmployee === "Yes" && !formData.employeeNumber) return false;

    if (isFieldVisible("sponsorHospital") && !formData.sponsorHospital) return false;

    if (isFieldVisible("dea")) {
      if (!formData.dea || !formData.npi || !formData.businessAddress || !formData.businessPhone) return false;
    }
    return true;
  };

  const step1Valid = () => {
    // ✅ Assignment required only if eligible; otherwise must be Medical HQ
    if (isAssignmentEligible()) {
      if (formData.assignment.length === 0) return false;
    } else {
      if (!Array.isArray(formData.assignment) || !formData.assignment.includes("Medical Headquarters")) return false;
    }

    if (formData.availability.length === 0) return false;
    if (!formData.shirtSize) return false;
    if (!formData.workedBefore) return false;
    if (formData.workedBefore === "Yes" && !formData.pastWorkYear) return false;
    return true;
  };

  const validateBeforeSubmit = () => {
    if (!step0Valid()) {
      setStatus("❌ Please complete all required fields in Step 1.");
      return false;
    }
    if (!step1Valid()) {
      setStatus("❌ Please complete all required fields in Step 2.");
      return false;
    }
    return true;
  };

  // ---------- Submit / Update (via callables) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailKey = normalizeEmail(formData.email || email);
    if (!emailKey) return setStatus("❌ Email required.");
    if (!validateBeforeSubmit()) return;

    setBusy(true);
    setStatus("Submitting...");

    try {
      await submitApplicationPublic({
        application: {
          ...formData,
          email: emailKey,
          emailKey,
          pinLast4: String(formData.ssn || "").trim(),
        },
      });

      showConfirmation(
        "Application Submitted",
        `Thank you for your interest in joining our team!

Submitting an application does not guarantee a position. If selected, we will try to accommodate your requested assignments, but assignments are based on operational needs.

If selected, you will be asked to upload your certifications and license at a later date. Please do not email these documents now.
Paramedics will also be asked to provide proof of current medical control in good standing.

The schedule is expected to be released on or around May 1st. You will only be contacted if you are selected.

Questions?
admin@travelerschampmedical.com

Thank you again for your interest — we appreciate you taking the time to apply.`
      );
    } catch (err) {
      console.error(err);
      const code = err?.code || "";
      if (String(code).includes("already-exists")) {
        setStatus("❌ An application has already been submitted using this email.");
        setExistingDocId(emailKey);
        setShowEditPrompt(true);
        setPinPrompt(false);
        setEditMode(false);
      } else {
        setStatus(`❌ Failed to submit. ${code ? `(${code})` : ""}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async () => {
    const emailKey = normalizeEmail(existingDocId || formData.email || email);
    if (!emailKey) return;
    if (!validateBeforeSubmit()) return;

    const pinToUse =
      String(formData.ssn || "").trim() ||
      String(existingDocRef.current?.pinLast4 || existingDocRef.current?.ssn || "").trim() ||
      String(pin || "").trim();

    if (!pinToUse || String(pinToUse).length !== 4) {
      setStatus("❌ Missing PIN. Please re-verify your application.");
      setEditMode(false);
      setEmailChecked(false);
      setShowEditPrompt(true);
      setPinPrompt(true);
      return;
    }

    setBusy(true);
    setStatus("Saving changes...");

    try {
      await updateApplicationPublic({
        email: emailKey,
        pin: pinToUse,
        application: {
          ...formData,
          email: emailKey,
          emailKey,
          pinLast4: pinToUse,
        },
      });

      showConfirmation("Changes Saved", "✅ Updated Application Saved.");
    } catch (err) {
      console.error(err);
      setStatus(`❌ Failed to update. ${err?.code ? `(${err.code})` : ""}`);
    } finally {
      setBusy(false);
    }
  };

  // ---------- Step pill ----------
  const StepPill = ({ idx, label }) => {
    const active = idx === step;
    const done = idx < step;

    const bg = done ? COLORS.ink : active ? "rgba(91,45,144,0.10)" : "#fff";
    const border = active ? `2px solid rgba(91,45,144,0.55)` : `1px solid ${COLORS.border}`;
    const color = done ? "#fff" : COLORS.ink;

    return (
      <div style={{ ...pill, background: bg, border, color }}>
        {done ? "✓ " : ""}
        {label}
      </div>
    );
  };

  // ---------- Confirmation screen ----------
  if (confirm.show) {
    return (
      <div style={confirmWrap}>
        <div style={{ ...card, width: "100%", maxWidth: 720 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 900, color: COLORS.ink }}>{confirm.title}</div>
            </div>

            <button type="button" onClick={closeAfterSuccess} style={btnPrimarySmall}>
              Close
            </button>
          </div>

          <div style={confirmBody}>
            <p style={confirmP}>Thank you for your interest in joining our team!</p>

            <p style={confirmP}>
              <strong>Submitting an application does not guarantee a position.</strong> If selected, we will try to
              accommodate your requested assignments, but assignments are based on operational needs.
            </p>

            <p style={confirmP}>
              If selected, you will be asked to upload your certifications and license at a later date.{" "}
              <strong>Please do not email these documents now.</strong>
              <br />
              <strong>Paramedics</strong> will also be asked to provide proof of current medical control in good standing.
            </p>

            <p style={confirmP}>
              The schedule is expected to be released on or around <strong>May 1st</strong>.{" "}
              <strong>You will only be contacted if you are selected.</strong>
            </p>

            <div style={confirmQuestionsBox}>
              <div style={{ fontWeight: 900, color: COLORS.ink }}>Questions?</div>
              <a href="mailto:admin@travelerschampmedical.com" style={confirmEmailLink}>
                admin@travelerschampmedical.com
              </a>
            </div>

            <div style={confirmFooter}>Thank you again for your interest — we appreciate you taking the time to apply.</div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Render ----------
  return (
    <div className="application-form" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Email gate only if NOT started from modal */}
      {!emailChecked && !autoStarted && (
        <div style={card}>
          <div style={titleRow}>
            <div>
              <div style={h2}>Join our Team</div>
              <div style={subText}>Enter your email to start a new application or edit an existing one.</div>
            </div>
            <div style={badge}>Trinity Health</div>
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={input}
              placeholder="you@example.com"
              disabled={busy}
            />

            <button type="button" onClick={() => handleEmailCheck()} style={btnPrimary} disabled={busy}>
              {busy ? "Checking…" : "Continue"}
            </button>

            {status && <div style={statusText}>{status}</div>}
          </div>
        </div>
      )}

      {/* Loading card while auto-start checks */}
      {autoStarted && !emailChecked && !showEditPrompt && !pinPrompt && !confirm.show && (
        <div style={card}>
          <div style={h3}>Loading…</div>
          <div style={subText}>{`Checking your application for ${email || "your email"}.`}</div>
          {status && <div style={statusText}>{status}</div>}
        </div>
      )}

      {showEditPrompt && !pinPrompt && !editMode && (
        <div style={card}>
          <div style={h3}>Application Found</div>
          <p style={{ marginTop: 8, marginBottom: 0, color: COLORS.slate, fontWeight: 800 }}>
            An application has already been submitted using this email. Would you like to edit it?
          </p>

          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <button type="button" onClick={() => handleEditChoice("yes")} style={btnPrimarySmall}>
              Yes, Edit
            </button>
            <button type="button" onClick={() => handleEditChoice("no")} style={btnGhostSmall}>
              No, Cancel
            </button>
          </div>
        </div>
      )}

      {pinPrompt && !editMode && (
        <div style={card}>
          <div style={h3}>Verify PIN</div>
          <div style={subText}>Enter the 4-digit PIN you used when you submitted (last 4 of SSN).</div>

          <label style={label}>4-digit PIN</label>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            maxLength={4}
            required
            inputMode="numeric"
            autoComplete="one-time-code"
            style={input}
          />

          <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <button type="button" onClick={handlePINVerify} style={btnPrimarySmall} disabled={busy}>
              {busy ? "Verifying…" : "Verify"}
            </button>
            <button
              type="button"
              onClick={() => {
                setPinPrompt(false);
                setShowEditPrompt(true);
                setStatus("");
                scrollModalToTop();
              }}
              style={btnGhostSmall}
              disabled={busy}
            >
              Back
            </button>
          </div>

          {status && <div style={statusText}>{status}</div>}
        </div>
      )}

      {emailChecked && (
        <>
          <div style={card}>
            <div style={titleRow}>
              <div>
                <div style={h2}>
                  Application {editMode ? <span style={{ color: COLORS.purple }}>(Edit Mode)</span> : ""}
                </div>
                <div style={subText}>Complete the steps below. Review before submitting.</div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <StepPill idx={0} label="Step 1: Info" />
                <StepPill idx={1} label="Step 2: Preferences" />
                <StepPill idx={2} label="Step 3: Review" />
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* STEP 1 */}
            {step === 0 && (
              <div style={card}>
                <div style={sectionTitle}>Step 1 — Contact & Credentials</div>

                <div style={grid2}>
                  <div>
                    <label style={label}>Name</label>
                    <input name="name" value={formData.name} onChange={handleChange} style={input} required />
                  </div>

                  <div>
                    <label style={label}>Phone</label>
                    <input name="phone" value={formData.phone} onChange={handleChange} style={input} required />
                  </div>

                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={label}>Address</label>
                    <input name="address" value={formData.address} onChange={handleChange} style={input} required />
                  </div>

                  <div>
                    <label style={label}>Last 4 of SSN (PIN)</label>
                    <input
                      name="ssn"
                      value={formData.ssn}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, ssn: e.target.value.replace(/\D/g, "").slice(0, 4) }))
                      }
                      maxLength={4}
                      required
                      inputMode="numeric"
                      style={input}
                    />
                    <div style={hint}>Used to edit your application later.</div>
                  </div>

                  <div>
                    <label style={label}>Are you a Trinity Health Employee?</label>
                    <select
                      name="isEmployee"
                      value={formData.isEmployee}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData((p) => ({
                          ...p,
                          isEmployee: val,
                          employeeNumber: val === "Yes" ? p.employeeNumber : "",
                        }));
                      }}
                      style={input}
                      required
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>

                  {formData.isEmployee === "Yes" && (
                    <div>
                      <label style={label}>Please enter your employee number</label>
                      <input
                        name="employeeNumber"
                        value={formData.employeeNumber}
                        onChange={handleChange}
                        style={input}
                        required
                      />
                    </div>
                  )}

                  <div>
                    <label style={label}>Position</label>
                    <select name="position" value={formData.position} onChange={handleChange} style={input} required>
                      <option value="">Select</option>
                      {positions.map((pos) => (
                        <option key={pos} value={pos}>
                          {pos}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {isFieldVisible("sponsorHospital") && (
                  <div style={{ marginTop: 12 }}>
                    <label style={label}>Sponsor Hospital</label>
                    <input
                      name="sponsorHospital"
                      value={formData.sponsorHospital}
                      onChange={handleChange}
                      style={input}
                      required
                    />
                  </div>
                )}

                {isFieldVisible("dea") && (
                  <div style={{ marginTop: 12 }}>
                    <div style={sectionSubTitle}>Physician Business Info</div>
                    <div style={grid2}>
                      <div>
                        <label style={label}>DEA #</label>
                        <input name="dea" value={formData.dea} onChange={handleChange} style={input} required />
                      </div>
                      <div>
                        <label style={label}>NPI #</label>
                        <input name="npi" value={formData.npi} onChange={handleChange} style={input} required />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <label style={label}>Business Address</label>
                        <input
                          name="businessAddress"
                          value={formData.businessAddress}
                          onChange={handleChange}
                          style={input}
                          required
                        />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <label style={label}>Business Phone</label>
                        <input
                          name="businessPhone"
                          value={formData.businessPhone}
                          onChange={handleChange}
                          style={input}
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div style={navRow}>
                  <button type="button" style={btnGhostSmall} onClick={resetAll}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={btnPrimarySmall}
                    onClick={() => {
                      if (!step0Valid()) {
                        setStatus("❌ Please complete all required fields before continuing.");
                        scrollModalToTop();
                        return;
                      }
                      setStatus("");
                      setStep(1);
                    }}
                  >
                    Next
                  </button>
                </div>

                {status && <div style={statusText}>{status}</div>}
              </div>
            )}

            {/* STEP 2 */}
            {step === 1 && (
              <div style={card}>
                <div style={sectionTitle}>Step 2 — Preferences</div>

                {isAssignmentEligible() ? (
                  <>
                    <div style={sectionSubTitle}>Assignment Preferences (select at least one)</div>
                    <div style={buttonGrid}>
                      {assignmentOptions.map((opt) => {
                        const selected = formData.assignment.includes(opt);
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => toggleInArray("assignment", opt)}
                            style={selected ? toggleBtnOnBold : toggleBtnOffBold}
                          >
                            <span style={selected ? checkMarkOn : checkMarkOff}>{selected ? "✓" : ""}</span>
                            <span>{opt}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div style={{ ...softPanel, marginBottom: 12 }}>
                    <div style={{ fontWeight: 900, color: COLORS.ink }}>Assignment</div>
                    <div style={{ marginTop: 6, fontWeight: 800, color: "#475569" }}>
                      Based on your position, you will be assigned to <strong>Medical Headquarters</strong>.
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 14 }}>
                  <div style={sectionSubTitle}>Availability (select at least one)</div>

                  <div className="button-row">
                    <button
                      type="button"
                      onClick={toggleAvailabilitySelectAll}
                      style={
                        Array.isArray(formData.availability) &&
                        formData.availability.length === availabilityOptions.length
                          ? toggleBtnOnBold
                          : toggleBtnOffBold
                      }
                    >
                      <span
                        style={
                          Array.isArray(formData.availability) &&
                          formData.availability.length === availabilityOptions.length
                            ? checkMarkOn
                            : checkMarkOff
                        }
                      >
                        {Array.isArray(formData.availability) &&
                        formData.availability.length === availabilityOptions.length
                          ? "✓"
                          : ""}
                      </span>
                      <span>
                        {Array.isArray(formData.availability) &&
                        formData.availability.length === availabilityOptions.length
                          ? "Clear All"
                          : "Select All"}
                      </span>
                    </button>
                  </div>

                  <div style={buttonGrid}>
                    {availabilityOptions.map((opt) => {
                      const selected = formData.availability.includes(opt);
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => toggleInArray("availability", opt)}
                          style={selected ? toggleBtnOnBold : toggleBtnOffBold}
                        >
                          <span style={selected ? checkMarkOn : checkMarkOff}>{selected ? "✓" : ""}</span>
                          <span>{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div style={{ marginTop: 14, ...grid2 }}>
                  <div>
                    <label style={label}>Shirt Size</label>
                    <select name="shirtSize" value={formData.shirtSize} onChange={handleChange} style={input} required>
                      <option value="">Select</option>
                      {shirtSizes.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={label}>Worked Previous Year?</label>
                    <select
                      name="workedBefore"
                      value={formData.workedBefore}
                      onChange={handleChange}
                      style={input}
                      required
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>

                  {formData.workedBefore === "Yes" && (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={label}>What year?</label>
                      <input
                        name="pastWorkYear"
                        value={formData.pastWorkYear}
                        onChange={handleChange}
                        style={input}
                        required
                      />
                    </div>
                  )}

                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={label}>Comments (optional)</label>
                    <textarea
                      name="comments"
                      value={formData.comments}
                      onChange={handleChange}
                      style={{ ...input, minHeight: 110 }}
                    />
                  </div>
                </div>

                <div style={navRow}>
                  <button type="button" style={btnGhostSmall} onClick={() => setStep(0)} disabled={busy}>
                    Back
                  </button>
                  <button
                    type="button"
                    style={btnPrimarySmall}
                    onClick={() => {
                      if (!step1Valid()) {
                        setStatus("❌ Please complete required preferences before continuing.");
                        scrollModalToTop();
                        return;
                      }
                      setStatus("");
                      setStep(2);
                    }}
                    disabled={busy}
                  >
                    Next
                  </button>
                </div>

                {status && <div style={statusText}>{status}</div>}
              </div>
            )}

            {/* STEP 3 */}
            {step === 2 && (
              <div style={card}>
                <div style={sectionTitle}>Step 3 — Review</div>

                <div style={reviewBox}>
                  <ReviewList label="Assignments" items={formData.assignment} order={assignmentOptions} />
                  <ReviewList label="Availability" items={formData.availability} order={availabilityOptions} />

                  <ReviewRow label="Shirt Size" value={formData.shirtSize} />
                  <ReviewRow
                    label="Worked Before"
                    value={
                      formData.workedBefore === "Yes"
                        ? `Yes (${formData.pastWorkYear || ""})`
                        : formData.workedBefore
                    }
                  />
                </div>

                {status && <div style={statusText}>{status}</div>}

                <div style={navRow}>
                  <button type="button" style={btnGhostSmall} onClick={() => setStep(1)} disabled={busy}>
                    Back
                  </button>

                  {editMode ? (
                    <button type="button" style={btnPrimarySmall} onClick={handleUpdate} disabled={busy}>
                      {busy ? "Saving…" : "Confirm Changes"}
                    </button>
                  ) : (
                    <button type="submit" style={btnPrimarySmall} disabled={busy}>
                      {busy ? "Submitting…" : "Submit Application"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </form>
        </>
      )}
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div style={reviewRow}>
      <div style={reviewLabel}>{label}</div>
      <div style={reviewVal}>{value || "—"}</div>
    </div>
  );
}

function ReviewList({ label, items, order = [] }) {
  const safe = Array.isArray(items) ? items : [];
  const sorted = order.length ? order.filter((opt) => safe.includes(opt)) : safe;

  return (
    <div style={reviewRow}>
      <div style={reviewLabel}>{label}</div>
      <div style={reviewVal}>
        {sorted.length === 0 ? (
          "—"
        ) : (
          <div style={pillWrap}>
            {sorted.map((it) => (
              <span key={it} style={pillChip}>
                {it}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Styles (inline) ---------- */

const card = {
  background: COLORS.bg,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 16,
  padding: 16,
  boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
};

const softPanel = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  padding: 14,
  background: "linear-gradient(90deg, rgba(91,45,144,0.06), rgba(47,161,92,0.05))",
};

const titleRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  flexWrap: "wrap",
};

const h2 = { fontSize: 18, fontWeight: 900, color: COLORS.ink };
const h3 = { fontSize: 16, fontWeight: 900, color: COLORS.ink };
const subText = { marginTop: 6, color: "#64748B", fontWeight: 700 };

const badge = {
  padding: "6px 10px",
  borderRadius: 999,
  background: `linear-gradient(90deg, rgba(91,45,144,0.12), rgba(47,161,92,0.10))`,
  border: `1px solid rgba(91,45,144,0.25)`,
  color: COLORS.ink,
  fontWeight: 900,
  fontSize: 12,
};

const label = {
  display: "block",
  fontWeight: 900,
  fontSize: 12,
  color: COLORS.ink,
  marginTop: 12,
  marginBottom: 6,
};

const input = {
  width: "100%",
  padding: "11px 12px",
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  outline: "none",
  fontSize: 16,
  background: "#fff",
};

const hint = { marginTop: 6, fontSize: 12, color: "#64748B", fontWeight: 700 };

const statusText = { marginTop: 10, fontWeight: 900, color: COLORS.danger };

const btnPrimary = {
  width: "100%",
  marginTop: 12,
  padding: "12px 14px",
  borderRadius: 12,
  border: `1px solid ${COLORS.purple}`,
  background: `linear-gradient(90deg, ${COLORS.purple}, ${COLORS.green})`,
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
};

const btnPrimarySmall = {
  padding: "10px 14px",
  borderRadius: 12,
  border: `1px solid ${COLORS.purple}`,
  background: `linear-gradient(90deg, ${COLORS.purple}, ${COLORS.green})`,
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
};

const btnGhostSmall = {
  padding: "10px 14px",
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.ink,
  fontWeight: 900,
  cursor: "pointer",
};

const pill = { padding: "8px 12px", borderRadius: 999, fontWeight: 900, fontSize: 12 };

const sectionTitle = { fontWeight: 900, fontSize: 16, marginBottom: 10, color: COLORS.ink };
const sectionSubTitle = { fontWeight: 900, fontSize: 13, color: COLORS.ink, marginBottom: 8 };

const grid2 = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 12,
};

const buttonGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", // ✅ FIXED
  gap: 10,
};

const navRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  marginTop: 16,
  flexWrap: "wrap",
};

const toggleBtnOffBold = {
  display: "flex",
  alignItems: "center",
  padding: "14px 14px",
  borderRadius: 14,
  border: `2px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.ink,
  fontWeight: 900,
  cursor: "pointer",
  textAlign: "left",
  width: "100%",
};

const toggleBtnOnBold = {
  display: "flex",
  alignItems: "center",
  padding: "14px 14px",
  borderRadius: 14,
  border: `2px solid ${COLORS.purple}`,
  background: `linear-gradient(90deg, rgba(91,45,144,0.16), rgba(47,161,92,0.12))`,
  color: COLORS.ink,
  fontWeight: 900,
  cursor: "pointer",
  textAlign: "left",
  boxShadow: "0 10px 22px rgba(0,0,0,0.08)",
  width: "100%",
};

const checkMarkBase = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  borderRadius: 9,
  fontWeight: 900,
  marginRight: 10,
  flexShrink: 0,
};

const checkMarkOff = {
  ...checkMarkBase,
  border: `2px solid ${COLORS.border}`,
  color: "transparent",
  background: "#fff",
};

const checkMarkOn = {
  ...checkMarkBase,
  border: `2px solid ${COLORS.purple}`,
  color: "#fff",
  background: `linear-gradient(90deg, ${COLORS.purple}, ${COLORS.green})`,
};

const pillWrap = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  alignItems: "flex-start",
};

const pillChip = {
  padding: "8px 10px",
  borderRadius: 999,
  border: "1px solid rgba(91,45,144,0.25)",
  background: "linear-gradient(90deg, rgba(91,45,144,0.10), rgba(47,161,92,0.08))",
  fontWeight: 900,
  color: "#0F172A",
  fontSize: 12,
};

const reviewBox = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 16,
  padding: 14,
  background: COLORS.soft,
};

const reviewRow = {
  display: "grid",
  gridTemplateColumns: "minmax(110px, 160px) 1fr",
  gap: 10,
  padding: "10px 0",
  borderBottom: "1px solid #eaeaea",
};

const confirmWrap = {
  display: "flex",
  justifyContent: "center",
  padding: 12,
};

const confirmBody = {
  marginTop: 12,
  color: COLORS.slate,
  fontWeight: 600,
  lineHeight: 1.6,
};

const confirmP = {
  margin: "10px 0",
};

const confirmQuestionsBox = {
  marginTop: 14,
  padding: 12,
  borderRadius: 14,
  border: `1px solid ${COLORS.border}`,
  background: "linear-gradient(90deg, rgba(91,45,144,0.06), rgba(47,161,92,0.05))",
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const confirmEmailLink = {
  fontWeight: 900,
  color: COLORS.purple,
  textDecoration: "none",
  wordBreak: "break-word",
};

const confirmFooter = {
  marginTop: 14,
  fontWeight: 700,
  color: "#475569",
};

const reviewLabel = { fontWeight: 900, color: COLORS.ink };
const reviewVal = { color: COLORS.slate, fontWeight: 800 };